define('businessServices/signup/signupSessionStore',[
    'businessServices/authentication/stores/signupAuthenticationStore',
    'businessServices/signup/signupSessionSingleton',
    'common/authentication/authenticatedSignup',
    'common/promises/promiseFactory',
    'common/storage/browserTabStorage',
    'constants/billingPackageTypeConstants',
    'constants/signupNumberTypeSelectionConstants',
    'entities/signupEntity',
    'persistence/dataProviders/signupDataProvider',
    'persistence/repositories/signupRepository',
], () => {
    return function (){
        let self = this;

        let _authenticatedSignup = null;
        let _signupAuthenticationStore = null;
        let _signupSessionDataProvider = null;
        let _signupSessionRepository = null;

        const PromiseFactoryConstructor = require('common/promises/promiseFactory');
        const _promiseFactory = new PromiseFactoryConstructor();
        const _browserTabStorage = require('common/storage/browserTabStorage');
        const _signupSessionState = require('businessServices/signup/signupSessionSingleton');
        const _billingPackageTypes = require('constants/billingPackageTypeConstants');
        const _signupNumberTypeSelectionConstants = require('constants/signupNumberTypeSelectionConstants');
        const _signupSocket = require('externalDependencies/clientWebSocket').forApplication("signup");

        const ONBOARDING_SELECTION_STORAGE_NAME = "TrestaSignupOnboardingSelection";
        const PACKAGE_DEEP_LINKED_STORAGE_NAME = "TrestaSignupPackageDeepLinked";

        const _saveOnboardingSelection = (onboardingSelection) => {
            _signupSessionState.onboardingSelection(onboardingSelection);
            _browserTabStorage.saveObject(ONBOARDING_SELECTION_STORAGE_NAME, {onboardingSelection: onboardingSelection});
        };

        const _clearOnboardingSelection = () => {
            _signupSessionState.onboardingSelection('');
            _browserTabStorage.deleteObject(ONBOARDING_SELECTION_STORAGE_NAME);
        };

        const _restoreOnboardingSelection = () => {
            const storedSelection = _browserTabStorage.getObject(ONBOARDING_SELECTION_STORAGE_NAME);
            if (storedSelection === null) {
                return;
            }

            _signupSessionState.onboardingSelection(storedSelection.onboardingSelection);
        };

        // Kept in tab storage because Back to the plans page drops the deep link's query string,
        // and the plans heading has to stay the same for the whole signup.
        const _savePackageDeepLinked = () => {
            _signupSessionState.isPackageDeepLinked(true);
            _browserTabStorage.saveObject(PACKAGE_DEEP_LINKED_STORAGE_NAME, {isPackageDeepLinked: true});
        };

        const _clearPackageDeepLinked = () => {
            _signupSessionState.isPackageDeepLinked(false);
            _browserTabStorage.deleteObject(PACKAGE_DEEP_LINKED_STORAGE_NAME);
        };

        const _restorePackageDeepLinked = () => {
            const stored = _browserTabStorage.getObject(PACKAGE_DEEP_LINKED_STORAGE_NAME);
            _signupSessionState.isPackageDeepLinked(stored !== null && stored.isPackageDeepLinked === true);
        };

        const _generateNewSignup = (createIfMissing) => {
            return _promiseFactory.defer((deferredObject) => {
                if (createIfMissing === false) {
                    deferredObject.resolve(null);
                } else {
                    _signupSessionRepository.createNew()
                        .fail(deferredObject.reject)
                        .done((newSignup) => {
                            _signupAuthenticationStore.save(newSignup.signupSessionId, null, newSignup.secretKey);
                            _signupSessionState.authorizedSignature = '';
                            _signupSessionState.headerCarrier = ko.observable('');
                            _signupSessionState.headerPhoneNumbers = ko.observable([]);
                            _signupSessionState.isAcknowledged = false;
                            _signupSessionState.isLoaded = true;
                            _signupSessionState.isBypassNumberSelection(false);
                            _signupSessionState.numberIntentSelection(newSignup.numberIntentSelection);
                            _signupSessionState.numberTypeSelection(newSignup.numberTypeSelection);
                            _clearOnboardingSelection();
                            _clearPackageDeepLinked();
                            _signupSessionState.password = null;
                            _signupSessionState.signupDocument = newSignup;
                            deferredObject.resolve(newSignup);
                        });
                }
            });
        };

        const _loadSignupSession = (createIfMissing) => {
            return _promiseFactory.defer((deferredObject) => {
                _signupAuthenticationStore.load()
                    .fail(deferredObject.reject)
                    .done((signupSessionId, accountId, secretKey) => {
                        if (signupSessionId === null) {
                            _generateNewSignup(createIfMissing)
                                .fail(deferredObject.reject)
                                .done(deferredObject.resolve);
                        } else {
                            _signupSessionDataProvider.getById(signupSessionId, secretKey)
                                .fail(deferredObject.reject)
                                .done((signupResult) => {
                                    if (signupResult && signupResult.isSuccessful) {
                                        _signupSessionState.signupDocument = signupResult.signupDocument;
                                        _signupSessionState.signupDocument.numberPort = signupResult.numberPortDocument;
                                        _signupSessionState.numberTypeSelection(signupResult.signupDocument.numberTypeSelection);
                                        switch (signupResult.signupDocument.numberTypeSelection) {
                                            case  _signupNumberTypeSelectionConstants.local:
                                            case  _signupNumberTypeSelectionConstants.tollFree:
                                                if (signupResult.signupDocument.reservedPhoneNumber){
                                                    _signupSessionState.headerPhoneNumbers([signupResult.signupDocument.reservedPhoneNumber]);
                                                }
                                                break;
                                            case  _signupNumberTypeSelectionConstants.port:
                                                _signupSessionState.headerCarrier(signupResult.numberPortDocument.carrier);
                                                if (signupResult.numberPortDocument.phoneNumbersToPort) {
                                                    _signupSessionState.headerPhoneNumbers(signupResult.numberPortDocument.phoneNumbersToPort.map(n => n.phoneNumber));
                                                }
                                                break;
                                            default:
                                                _signupSessionState.headerPhoneNumbers([]);
                                                break;
                                        }
                                        _restoreOnboardingSelection();
                                        _restorePackageDeepLinked();
                                        _signupSessionState.isLoaded = true;
                                        deferredObject.resolve();
                                    } else {
                                        _generateNewSignup(createIfMissing)
                                            .fail(deferredObject.reject)
                                            .done(deferredObject.resolve);
                                    }
                                });
                        }
                    });
            });
        };
        const _clearSessionStateReservedPhoneNumber = () => {
            _signupSessionState.signupDocument.reservedPhoneNumber = null;
            _signupSessionState.signupDocument.reservedPhoneNumberRegion = null;
            _signupSessionState.signupDocument.reservedPhoneNumberCity = null;
            _signupSessionState.signupDocument.reservedPhoneNumberVanityString = null;
            _signupSessionState.signupDocument.reservedPhoneNumberSearchType = null;
            _signupSessionState.headerPhoneNumbers([]);
        };
        const _updateSessionStateReservedPhoneNumber = (signupSessionEntity) => {
            _signupSessionState.signupDocument.reservedPhoneNumber = signupSessionEntity.signupDocument.reservedPhoneNumber;
            _signupSessionState.signupDocument.reservedPhoneNumberRegion = signupSessionEntity.signupDocument.reservedPhoneNumberRegion;
            _signupSessionState.signupDocument.reservedPhoneNumberCity = signupSessionEntity.signupDocument.reservedPhoneNumberCity;
            _signupSessionState.signupDocument.reservedPhoneNumberVanityString = signupSessionEntity.signupDocument.reservedPhoneNumberVanityString;
            _signupSessionState.signupDocument.reservedPhoneNumberSearchType = signupSessionEntity.signupDocument.reservedPhoneNumberSearchType;
            _signupSessionState.signupDocument.numberTypeSelection = signupSessionEntity.signupDocument.numberTypeSelection;
            _signupSessionState.signupDocument.numberIntentSelection = signupSessionEntity.signupDocument.numberIntentSelection;

            _signupSessionState.numberIntentSelection(signupSessionEntity.signupDocument.numberIntentSelection);
            _signupSessionState.numberTypeSelection(signupSessionEntity.signupDocument.numberTypeSelection);
            _signupSessionState.headerPhoneNumbers([signupSessionEntity.signupDocument.reservedPhoneNumber]);
        };
        self.cancelPhoneNumberReservation = (rawPhoneNumber) => {
            const signupSessionId = _authenticatedSignup.signupSessionId;
            const secretKey = _authenticatedSignup.secretKey;
            return _promiseFactory.defer((promise) => {
                _signupSessionRepository.cancelReservation(signupSessionId, secretKey, rawPhoneNumber)
                    .fail(promise.reject)
                    .done(() => {
                        _clearSessionStateReservedPhoneNumber();
                        promise.resolve();
                    });
            });
        };
        self.clear = () => {
            _signupSessionState.isLoaded = false;
            _signupSessionState.authorizedSignature = '';
            _signupSessionState.headerCarrier('');
            _signupSessionState.headerPhoneNumbers([]);
            _signupSessionState.isAcknowledged = false;
            _signupSessionState.isBypassNumberSelection(false);
            _signupSessionState.numberIntentSelection('');
            _signupSessionState.numberTypeSelection('');
            _clearOnboardingSelection();
            _clearPackageDeepLinked();
            _signupSessionState.packageTypeSelection('');
            _signupSessionState.password = null;
            _signupSessionState.signupDocument = null;
        };
        self.connectSocket = (signupSessionId, secretKey) => _promiseFactory.defer(promise => {
            _signupSessionDataProvider.connectSocket(signupSessionId, secretKey)
                .fail(promise.reject)
                .done(promise.resolve);
        });
        self.createNumberPort = () => {
            return _promiseFactory.defer((promise) => {
                const signupSessionId = _authenticatedSignup.signupSessionId;
                const secretKey = _authenticatedSignup.secretKey;
                _signupSessionRepository.createNumberPort(signupSessionId, secretKey)
                    .fail(promise.reject)
                    .done((numberPortEntity) => {
                        _signupSessionState.signupDocument.numberPort = numberPortEntity;
                        promise.resolve(numberPortEntity);
                    });
            });
        };
        self.generateNewSignup = (createIfMissing = true) => {
            return _generateNewSignup(createIfMissing);
        };
        self.get = (createIfMissing = true) => {
            return _promiseFactory.defer( (deferredObject) => {
                if (_signupSessionState.isLoaded === false) {
                    _loadSignupSession(createIfMissing)
                        .fail(deferredObject.reject)
                        .done(() => {
                            deferredObject.resolve(_signupSessionState.signupDocument);
                        });
                } else {
                    deferredObject.resolve(_signupSessionState.signupDocument);
                }
            });
        };
        self.getNumberType = () => _promiseFactory.deferIndefinitely(deferredObject => {
            self.get()
                .fail(deferredObject.reject)
                .done(signupDocument => {
                    _signupSessionState.numberTypeSelection(signupDocument.numberTypeSelection);
                    deferredObject.resolve(signupDocument.numberTypeSelection);
                });
        });
        self.getState = (forceLoad) => {
            return _promiseFactory.defer((promise) => {
                if (_signupSessionState.isLoaded === false || forceLoad === true) {
                    _loadSignupSession()
                        .fail(promise.reject)
                        .done(() => {
                            promise.resolve(_signupSessionState);
                        });
                } else {
                    promise.resolve(_signupSessionState);
                }
            });
        };
        self.getAffiliates = () => _promiseFactory.defer(promise => {
            const signupSessionId = _authenticatedSignup.signupSessionId;
            const secretKey = _authenticatedSignup.secretKey;
            _signupSessionDataProvider.getAffiliates(signupSessionId, secretKey)
                .fail(promise.reject)
                .done(promise.resolve);
        });
        // Derived here rather than by the caller so that every fetch of the list keeps the flag
        // current, and nothing downstream has to remember to set it.
        const _isTelephonyOnlyOffering = (billingPackages) => {
            return !billingPackages.some(billingPackage =>
                billingPackage.type === _billingPackageTypes.liveReceptionist.id ||
                billingPackage.type === _billingPackageTypes.webChat.id ||
                billingPackage.type === _billingPackageTypes.outboundCalling.id
            );
        };

        self.getBillingPackages = () => _promiseFactory.defer(promise => {
            const signupSessionId = _authenticatedSignup.signupSessionId;
            const secretKey = _authenticatedSignup.secretKey;
            _signupSessionDataProvider.getBillingPackages(signupSessionId, secretKey)
                .fail(promise.reject)
                .done((result) => {
                    const billingPackages = result && result.billingPackages;
                    if (Array.isArray(billingPackages)) {
                        _signupSessionState.isTelephonyOnlyOffering(_isTelephonyOnlyOffering(billingPackages));
                    }
                    promise.resolve(result);
                });
        });
        self.getHubSpotContact = (contactId) => {
            const signupSessionId = _authenticatedSignup.signupSessionId;
            const secretKey = _authenticatedSignup.secretKey;
            return _signupSessionDataProvider.getHubSpotContact(signupSessionId, secretKey, contactId);
        };
        self.getHubSpotContactByEmail = (emailAddress) => {
            const signupSessionId = _authenticatedSignup.signupSessionId;
            const secretKey = _authenticatedSignup.secretKey;
            return _signupSessionDataProvider.getHubSpotContactByEmail(signupSessionId, secretKey, emailAddress);
        };
        self.getOnboardingForms = () => _promiseFactory.defer(promise => {
            const signupSessionId = _authenticatedSignup.signupSessionId;
            const secretKey = _authenticatedSignup.secretKey;
            _signupSessionDataProvider.getOnboardingForms(signupSessionId, secretKey)
                .fail(promise.reject)
                .done(promise.resolve);
        });
        self.getPaymentMethod = () => _promiseFactory.defer(promise => {
            const signupSessionId = _authenticatedSignup.signupSessionId;
            const secretKey = _authenticatedSignup.secretKey;
            _signupSessionDataProvider.getPaymentMethod(signupSessionId, secretKey)
                .fail(promise.reject)
                .done(promise.resolve);
        });
        self.checkIsFraudulentDeviceOrIp = (requestId) => {
            const signupSessionId = _authenticatedSignup.signupSessionId;
            const secretKey = _authenticatedSignup.secretKey;
            if(requestId) {
                return _signupSessionRepository.checkIsFraudulentDevice(signupSessionId, secretKey, requestId);
            } else {
                return _signupSessionRepository.checkIsFraudulentIpAddress(signupSessionId, secretKey);
            }
        };
        self.isRestrictedPhoneNumber = (phoneNumber) => {
            const signupSessionId = _authenticatedSignup.signupSessionId;
            const secretKey = _authenticatedSignup.secretKey;
            return _signupSessionRepository.checkRestrictedPhoneNumber(signupSessionId, secretKey, phoneNumber);
        };
        self.isFraudulentPhoneNumber = (phoneNumber, countryCode) => {
            const signupSessionId = _authenticatedSignup.signupSessionId;
            const secretKey = _authenticatedSignup.secretKey;
            return _signupSessionRepository.checkIsFraudulentPhoneNumber(signupSessionId, secretKey, phoneNumber, countryCode);
        };
        self.reservePhoneNumber = ({phoneNumber, region, city, searchType, vanityString = null}) => {
            const {signupSessionId, secretKey} = _authenticatedSignup;
            return _promiseFactory.defer((deferredObject) => {
                _signupSessionRepository.reserveNumber({signupSessionId, secretKey, phoneNumber, region, city, searchType, vanityString})
                    .fail(deferredObject.reject)
                    .done((signupSessionEntity) => {
                        if (signupSessionEntity.status === "reserved") {
                            _updateSessionStateReservedPhoneNumber(signupSessionEntity);
                        }

                        deferredObject.resolve({
                            status: signupSessionEntity.status
                        });
                    });
            });
        };
        self.reserveLocalPhoneNumberWithoutLocation = ({phoneNumber, searchType, vanityString = null}) => {
            const {signupSessionId, secretKey} = _authenticatedSignup;
            return _promiseFactory.defer((deferredObject) => {
                _signupSessionRepository.reserveLocalPhoneNumberWithoutLocation({signupSessionId, secretKey, phoneNumber, searchType, vanityString})
                    .fail(deferredObject.reject)
                    .done((signupSessionEntity) => {
                        if (signupSessionEntity.status === "reserved") {
                            _updateSessionStateReservedPhoneNumber(signupSessionEntity);
                        }

                        deferredObject.resolve({
                            status: signupSessionEntity.status
                        });
                    });
            });
        };
        self.reReserveSessionPhoneNumber = () => {
            const signupSessionId = _authenticatedSignup.signupSessionId;
            const secretKey = _authenticatedSignup.secretKey;
            return _promiseFactory.defer((deferredObject) => {
                _signupSessionRepository.reReserveSessionPhoneNumber(signupSessionId, secretKey)
                    .fail(deferredObject.reject)
                    .done((signupSessionEntity) => {
                        if (signupSessionEntity.status === "reserved") {
                            _updateSessionStateReservedPhoneNumber(signupSessionEntity);
                        } else {
                            _clearSessionStateReservedPhoneNumber();
                        }

                        deferredObject.resolve({
                            status: signupSessionEntity.status
                        });
                    });
            });
        };
        self.checkIsFraudulentDevice = (requestId) => {
            const signupSessionId = _authenticatedSignup.signupSessionId;
            const secretKey = _authenticatedSignup.secretKey;
            return _signupSessionRepository.checkIsFraudulentDevice(signupSessionId, secretKey, requestId);
        };
        self.saveDeviceFingerPrint = (deviceFingerPrint) => {
            return _promiseFactory.defer((promise) => {
                const signupSessionId = _authenticatedSignup.signupSessionId;
                const secretKey = _authenticatedSignup.secretKey;
                _signupSessionRepository.saveDeviceFingerPrint(signupSessionId, secretKey, deviceFingerPrint)
                    .fail(promise.reject)
                    .done((signupSessionEntity) => {
                        _signupSessionState.signupDocument.deviceFingerPrint = signupSessionEntity.deviceFingerPrint;
                        promise.resolve();
                    });
            });
        };
        self.saveAffiliateId = (affiliateId) => {
            return _promiseFactory.defer((promise) => {
                const signupSessionId = _authenticatedSignup.signupSessionId;
                const secretKey = _authenticatedSignup.secretKey;
                _signupSessionRepository.saveAffiliateId(signupSessionId, secretKey, affiliateId)
                    .fail(promise.reject)
                    .done((signupSessionEntity) => {
                        _signupSessionState.signupDocument.affiliateId = signupSessionEntity.affiliateId;
                        _signupSessionState.signupDocument.planId = signupSessionEntity.planId;
                        _signupSessionState.signupDocument.telephonyPackageId = signupSessionEntity.telephonyPackageId;
                        _signupSessionState.signupDocument.liveReceptionistPackageId = signupSessionEntity.liveReceptionistPackageId;
                        promise.resolve();
                    });
            });
        };
        self.saveHubSpotContactRecordId = (hubSpotContactRecordId) => {
            return _promiseFactory.defer((promise) => {
                const signupSessionId = _authenticatedSignup.signupSessionId;
                const secretKey = _authenticatedSignup.secretKey;
                _signupSessionRepository.saveHubSpotContactRecordId(signupSessionId, secretKey, hubSpotContactRecordId)
                    .fail(promise.reject)
                    .done((signupSessionEntity) => {
                        _signupSessionState.signupDocument.hubSpotContactRecordId = signupSessionEntity.hubSpotContactRecordId;
                        promise.resolve();
                    });
            });
        };
        self.savePatLiveAccountNumber = (patLiveAccountNumber) => {
            return _promiseFactory.defer((promise) => {
                const signupSessionId = _authenticatedSignup.signupSessionId;
                const secretKey = _authenticatedSignup.secretKey;
                _signupSessionRepository.savePatLiveAccountNumber(signupSessionId, secretKey, patLiveAccountNumber)
                    .fail(promise.reject)
                    .done((signupSessionEntity) => {
                        _signupSessionState.signupDocument.patLiveAccountNumber = signupSessionEntity.patLiveAccountNumber;
                        promise.resolve();
                    });
            });
        };
        self.saveShouldBypassWelcomeEmail = (shouldBypassWelcomeEmail) => {
            return _promiseFactory.defer((promise) => {
                const signupSessionId = _authenticatedSignup.signupSessionId;
                const secretKey = _authenticatedSignup.secretKey;
                _signupSessionRepository.saveShouldBypassWelcomeEmail(signupSessionId, secretKey, shouldBypassWelcomeEmail)
                    .fail(promise.reject)
                    .done((signupSessionEntity) => {
                        _signupSessionState.signupDocument.shouldBypassWelcomeEmail = signupSessionEntity.shouldBypassWelcomeEmail;
                        promise.resolve();
                    });
            });
        };

        self.saveBusinessName = (businessName) => {
            return _promiseFactory.defer((promise) => {
                const signupSessionId = _authenticatedSignup.signupSessionId;
                const secretKey = _authenticatedSignup.secretKey;
                _signupSessionRepository.saveBusinessName(signupSessionId, secretKey, businessName)
                    .fail(promise.reject)
                    .done((signupSessionEntity) => {
                        _signupSessionState.signupDocument.businessName = signupSessionEntity.businessName;
                        promise.resolve();
                    });
            });
        };
        self.isFraudulentEmailAddress = (emailAddress) => {
            const signupSessionId = _authenticatedSignup.signupSessionId;
            const secretKey = _authenticatedSignup.secretKey;
            return _signupSessionRepository.checkIsFraudulentEmailAddress(signupSessionId, secretKey, emailAddress);
        };
        self.saveEmailAddress = (emailAddress) => {
            return _promiseFactory.defer((promise) => {
                const signupSessionId = _authenticatedSignup.signupSessionId;
                const secretKey = _authenticatedSignup.secretKey;
                _signupSessionRepository.saveEmailAddress(signupSessionId, secretKey, emailAddress)
                    .fail(promise.reject)
                    .done((signupSessionEntity) => {
                        if (emailAddress !== _signupSessionState.signupDocument.emailAddress) {
                            _signupSessionState.signupDocument.isEmailCompleted = false;
                        }

                        _signupSessionState.signupDocument.emailAddress = signupSessionEntity.emailAddress;
                        _signupSessionState.signupDocument.isEmailArrowValid = false;
                        _signupSessionState.signupDocument.emailVerificationCodeSendCount = 0;
                        promise.resolve();
                    });
            });
        };
        self.saveFirstName = (firstName) => {
            return _promiseFactory.defer((promise) => {
                const signupSessionId = _authenticatedSignup.signupSessionId;
                const secretKey = _authenticatedSignup.secretKey;
                _signupSessionRepository.saveFirstName(signupSessionId, secretKey, firstName)
                    .fail(promise.reject)
                    .done((signupSessionEntity) => {
                        _signupSessionState.signupDocument.firstName = signupSessionEntity.firstName;
                        promise.resolve();
                    });
            });
        };
        self.saveFlow = (flow) => {
            return _promiseFactory.defer((promise) => {
                const signupSessionId = _authenticatedSignup.signupSessionId;
                const secretKey = _authenticatedSignup.secretKey;
                _signupSessionRepository.saveFlow(signupSessionId, secretKey, flow)
                    .fail(promise.reject)
                    .done((signupSessionEntity) => {
                        _signupSessionState.signupDocument.flow = signupSessionEntity.flow;
                        promise.resolve();
                    });
            });
        };
        self.saveHowDidYouHearAboutUs = (howDidYouHearAboutUs) => {
            return _promiseFactory.defer((promise) => {
                const signupSessionId = _authenticatedSignup.signupSessionId;
                const secretKey = _authenticatedSignup.secretKey;
                _signupSessionRepository.saveHowDidYouHearAboutUs(signupSessionId, secretKey, howDidYouHearAboutUs)
                    .fail(promise.reject)
                    .done((signupSessionEntity) => {
                        _signupSessionState.signupDocument.howDidYouHearAboutUs = signupSessionEntity.howDidYouHearAboutUs;
                        promise.resolve();
                    });
            });
        };
        self.saveHowDidYouHearAboutUsOther = (howDidYouHearAboutUsOther) => {
            return _promiseFactory.defer((promise) => {
                const signupSessionId = _authenticatedSignup.signupSessionId;
                const secretKey = _authenticatedSignup.secretKey;
                _signupSessionRepository.saveHowDidYouHearAboutUsOther(signupSessionId, secretKey, howDidYouHearAboutUsOther)
                    .fail(promise.reject)
                    .done((signupSessionEntity) => {
                        _signupSessionState.signupDocument.howDidYouHearAboutUsOther = signupSessionEntity.howDidYouHearAboutUsOther;
                        promise.resolve();
                    });
            });
        };
        self.saveLastName = (lastName) => {
            return _promiseFactory.defer((promise) => {
                const signupSessionId = _authenticatedSignup.signupSessionId;
                const secretKey = _authenticatedSignup.secretKey;
                _signupSessionRepository.saveLastName(signupSessionId, secretKey, lastName)
                    .fail(promise.reject)
                    .done((signupSessionEntity) => {
                        _signupSessionState.signupDocument.lastName = signupSessionEntity.lastName;
                        promise.resolve();
                    });
            });
        };
        self.saveMobileNumber = (mobileNumber) => {
            return _promiseFactory.defer((promise) => {
                const signupSessionId = _authenticatedSignup.signupSessionId;
                const secretKey = _authenticatedSignup.secretKey;
                _signupSessionRepository.saveMobileNumber(signupSessionId, secretKey, mobileNumber)
                    .fail(promise.reject)
                    .done((signupSessionEntity) => {
                        _signupSessionState.signupDocument.mobilePhoneNumber = signupSessionEntity.mobilePhoneNumber;
                        promise.resolve();
                    });
            });
        };
        self.saveMobileNumberCountryCode = (mobileNumberCountryCode) => {
            return _promiseFactory.defer((promise) => {
                const signupSessionId = _authenticatedSignup.signupSessionId;
                const secretKey = _authenticatedSignup.secretKey;
                _signupSessionRepository.saveMobileNumberCountryCode(signupSessionId, secretKey, mobileNumberCountryCode)
                    .fail(promise.reject)
                    .done((signupSessionEntity) => {
                        _signupSessionState.signupDocument.mobileNumberCountryCode = signupSessionEntity.mobileNumberCountryCode;
                        promise.resolve();
                    });
            });
        };
        self.saveNumberPort = (numberPort) => {
            return _promiseFactory.defer((promise) => {
                const signupSessionId = _authenticatedSignup.signupSessionId;
                const secretKey = _authenticatedSignup.secretKey;
                _signupSessionRepository.saveNumberPort(signupSessionId, secretKey, numberPort)
                    .fail(promise.reject)
                    .done((numberPortEntity) => {
                        _signupSessionState.signupDocument.numberPort = numberPortEntity;
                        _signupSessionState.headerPhoneNumbers(numberPortEntity.phoneNumbersToPort.map(n => n.phoneNumber));
                        _signupSessionState.headerCarrier(numberPortEntity.carrier);
                        promise.resolve();
                    });
            });
        };

        self.storeBypassNumberSelection = (isAgentBypass) => _signupSessionState.isBypassNumberSelection(isAgentBypass);
        self.storeNumberPortAuthorizedSignature = (authorizedSignature) => _signupSessionState.authorizedSignature = authorizedSignature;
        self.storeNumberPortIsAcknowledged = (isAcknowledged) => _signupSessionState.isAcknowledged = isAcknowledged;
        self.storeOnboardingSelection = (onboardingSelection) => _saveOnboardingSelection(onboardingSelection);
        self.storePackageDeepLinked = () => _savePackageDeepLinked();

        self.saveNumberPortBillingAccountNumber = (numberPortId, accountNumber) => {
            return _promiseFactory.defer((promise) => {
                const signupSessionId = _authenticatedSignup.signupSessionId;
                const secretKey = _authenticatedSignup.secretKey;
                _signupSessionRepository.saveNumberPortBillingAccountNumber(signupSessionId, secretKey, numberPortId, accountNumber)
                    .fail(promise.reject)
                    .done((numberPortEntity) => {
                        _signupSessionState.signupDocument.numberPort.billingAccountNumber = numberPortEntity.billingAccountNumber;
                        promise.resolve();
                    });
            });
        };
        self.saveNumberPortBillingAddressLine1 = (numberPortId, billingAddress) => {
            return _promiseFactory.defer((promise) => {
                const signupSessionId = _authenticatedSignup.signupSessionId;
                const secretKey = _authenticatedSignup.secretKey;
                _signupSessionRepository.saveNumberPortBillingAddressLine1(signupSessionId, secretKey, numberPortId, billingAddress)
                    .fail(promise.reject)
                    .done((numberPortEntity) => {
                        _signupSessionState.signupDocument.numberPort.billingAddressLine1 = numberPortEntity.billingAddressLine1;
                        promise.resolve();
                    });
            });
        };
        self.saveNumberPortBillingAddressLine2 = (numberPortId, billingApt) => {
            return _promiseFactory.defer((promise) => {
                const signupSessionId = _authenticatedSignup.signupSessionId;
                const secretKey = _authenticatedSignup.secretKey;
                _signupSessionRepository.saveNumberPortBillingAddressLine2(signupSessionId, secretKey, numberPortId, billingApt)
                    .fail(promise.reject)
                    .done((numberPortEntity) => {
                        _signupSessionState.signupDocument.numberPort.billingAddressLine2 = numberPortEntity.billingAddressLine2;
                        promise.resolve();
                    });
            });
        };
        self.saveNumberPortBillingCity = (numberPortId, billingCity) => {
            return _promiseFactory.defer((promise) => {
                const signupSessionId = _authenticatedSignup.signupSessionId;
                const secretKey = _authenticatedSignup.secretKey;
                _signupSessionRepository.saveNumberPortBillingCity(signupSessionId, secretKey, numberPortId, billingCity)
                    .fail(promise.reject)
                    .done((numberPortEntity) => {
                        _signupSessionState.signupDocument.numberPort.billingCity = numberPortEntity.billingCity;
                        promise.resolve();
                    });
            });
        };
        self.saveNumberPortBillingCountry = (numberPortId, countryAbbreviation) => {
            return _promiseFactory.defer((promise) => {
                const signupSessionId = _authenticatedSignup.signupSessionId;
                const secretKey = _authenticatedSignup.secretKey;
                _signupSessionRepository.saveNumberPortBillingCountry(signupSessionId, secretKey, numberPortId, countryAbbreviation)
                    .fail(promise.reject)
                    .done((numberPortEntity) => {
                        _signupSessionState.signupDocument.numberPort.billingCountry = numberPortEntity.billingCountry;
                        promise.resolve();
                    });
            });
        };
        self.saveNumberPortBillingName = (numberPortId, billingName) => {
            return _promiseFactory.defer((promise) => {
                const signupSessionId = _authenticatedSignup.signupSessionId;
                const secretKey = _authenticatedSignup.secretKey;
                _signupSessionRepository.saveNumberPortBillingName(signupSessionId, secretKey, numberPortId, billingName)
                    .fail(promise.reject)
                    .done((numberPortEntity) => {
                        _signupSessionState.signupDocument.numberPort.billingName = numberPortEntity.billingName;
                        promise.resolve();
                    });
            });
        };
        self.saveNumberPortBillingPinPasscode = (numberPortId, pinOrPasscode) => {
            return _promiseFactory.defer((promise) => {
                const signupSessionId = _authenticatedSignup.signupSessionId;
                const secretKey = _authenticatedSignup.secretKey;
                _signupSessionRepository.saveNumberPortBillingPinPasscode(signupSessionId, secretKey, numberPortId, pinOrPasscode)
                    .fail(promise.reject)
                    .done((numberPortEntity) => {
                        _signupSessionState.signupDocument.numberPort.billingPinPasscode = numberPortEntity.billingPinPasscode;
                        promise.resolve();
                    });
            });
        };
        self.saveNumberPortBillingPhoneNumber = (numberPortId, phoneNumber) => {
            return _promiseFactory.defer((promise) => {
                const signupSessionId = _authenticatedSignup.signupSessionId;
                const secretKey = _authenticatedSignup.secretKey;
                _signupSessionRepository.saveNumberPortBillingPhoneNumber(signupSessionId, secretKey, numberPortId, phoneNumber)
                    .fail(promise.reject)
                    .done((numberPortEntity) => {
                        _signupSessionState.signupDocument.numberPort.billingPhoneNumber = numberPortEntity.billingPhoneNumber;
                        promise.resolve();
                    });
            });
        };
        self.saveNumberPortBillingPhoneNumberCountry = (numberPortId, countryAbbreviation) => {
            return _promiseFactory.defer((promise) => {
                const signupSessionId = _authenticatedSignup.signupSessionId;
                const secretKey = _authenticatedSignup.secretKey;
                _signupSessionRepository.saveNumberPortBillingPhoneNumberCountry(signupSessionId, secretKey, numberPortId, countryAbbreviation)
                    .fail(promise.reject)
                    .done((numberPortEntity) => {
                        _signupSessionState.signupDocument.numberPort.billingPhoneNumberCountry = numberPortEntity.billingPhoneNumberCountry;
                        promise.resolve();
                    });
            });
        };
        self.saveNumberPortBillingState = (numberPortId, billingState) => {
            return _promiseFactory.defer((promise) => {
                const signupSessionId = _authenticatedSignup.signupSessionId;
                const secretKey = _authenticatedSignup.secretKey;
                _signupSessionRepository.saveNumberPortBillingState(signupSessionId, secretKey, numberPortId, billingState)
                    .fail(promise.reject)
                    .done((numberPortEntity) => {
                        _signupSessionState.signupDocument.numberPort.billingState = numberPortEntity.billingState;
                        promise.resolve();
                    });
            });
        };
        self.saveNumberPortBillingZipPostalCode = (numberPortId, zipPostalCode) => {
            return _promiseFactory.defer((promise) => {
                const signupSessionId = _authenticatedSignup.signupSessionId;
                const secretKey = _authenticatedSignup.secretKey;
                _signupSessionRepository.saveNumberPortBillingZipPostalCode(signupSessionId, secretKey, numberPortId, zipPostalCode)
                    .fail(promise.reject)
                    .done((numberPortEntity) => {
                        _signupSessionState.signupDocument.numberPort.billingZipPostalCode = numberPortEntity.billingZipPostalCode;
                        promise.resolve();
                    });
            });
        };
        self.saveNumberPortCarrier = (numberPortId, carrier) => {
            return _promiseFactory.defer((promise) => {
                const signupSessionId = _authenticatedSignup.signupSessionId;
                const secretKey = _authenticatedSignup.secretKey;
                _signupSessionRepository.saveNumberPortCarrier(signupSessionId, secretKey, numberPortId, carrier)
                    .fail(promise.reject)
                    .done((numberPortEntity) => {
                        _signupSessionState.signupDocument.numberPort.carrier = numberPortEntity.carrier;
                        _signupSessionState.headerCarrier(carrier);
                        promise.resolve();
                    });
            });
        };
        self.saveNumberPortDesiredPortDate = (numberPortId, portDate) => {
            return _promiseFactory.defer((promise) => {
                const signupSessionId = _authenticatedSignup.signupSessionId;
                const secretKey = _authenticatedSignup.secretKey;
                _signupSessionRepository.saveNumberPortDesiredPortDate(signupSessionId, secretKey, numberPortId, portDate)
                    .fail(promise.reject)
                    .done((numberPortEntity) => {
                        _signupSessionState.signupDocument.numberPort.desiredPortDate = numberPortEntity.desiredPortDate;
                        promise.resolve();
                    });
            });
        };
        self.saveNumberPortOtherNumbersOnAccount = (numberPortId, areOtherNumbersOnAccount) => {
            return _promiseFactory.defer((promise) => {
                const signupSessionId = _authenticatedSignup.signupSessionId;
                const secretKey = _authenticatedSignup.secretKey;
                _signupSessionRepository.saveNumberPortOtherNumbersOnAccount(signupSessionId, secretKey, numberPortId, areOtherNumbersOnAccount)
                    .fail(promise.reject)
                    .done((numberPortEntity) => {
                        _signupSessionState.signupDocument.numberPort.otherNumbersOnAccount = numberPortEntity.otherNumbersOnAccount;
                        promise.resolve();
                    });
            });
        };
        self.saveNumberPortPhoneNumbers = (numberPortId, numbersToPort) => {
            return _promiseFactory.defer((promise) => {
                const signupSessionId = _authenticatedSignup.signupSessionId;
                const secretKey = _authenticatedSignup.secretKey;
                _signupSessionRepository.saveNumberPortPhoneNumbers(signupSessionId, secretKey, numberPortId, numbersToPort)
                    .fail(promise.reject)
                    .done((numberPortEntity) => {
                        _signupSessionState.signupDocument.numberPort.phoneNumbersToPort = numberPortEntity.phoneNumbersToPort;
                        _signupSessionState.headerPhoneNumbers(numbersToPort.map(n => n.phoneNumber));
                        promise.resolve();
                    });
            });
        };
        self.saveNumberTypeSelection = (numberTypeSelection) => {
            return _promiseFactory.defer((promise) => {
                const signupSessionId = _authenticatedSignup.signupSessionId;
                const secretKey = _authenticatedSignup.secretKey;
                _signupSessionRepository.saveNumberTypeSelection(signupSessionId, secretKey, numberTypeSelection)
                    .fail(promise.reject)
                    .done(() => {
                        _signupSessionState.signupDocument.numberTypeSelection = numberTypeSelection;
                        _signupSessionState.numberTypeSelection(numberTypeSelection);
                        promise.resolve();
                    });
            });
        };
        self.saveNumberIntentSelection = (numberIntentSelection) => {
            return _promiseFactory.defer((promise) => {
                const signupSessionId = _authenticatedSignup.signupSessionId;
                const secretKey = _authenticatedSignup.secretKey;
                _signupSessionRepository.saveNumberIntentSelection(signupSessionId, secretKey, numberIntentSelection)
                    .fail(promise.reject)
                    .done(() => {
                        _signupSessionState.signupDocument.numberIntentSelection = numberIntentSelection;
                        _signupSessionState.numberIntentSelection(numberIntentSelection);
                        promise.resolve();
                    });
            });
        };
        self.savePackageTypeSelection = (packageTypeSelection) => {
            return _promiseFactory.defer((promise) => {
                _signupSessionState.packageTypeSelection(packageTypeSelection);
                promise.resolve();
            });
        };
        self.savePassword = (password) => {
            return _promiseFactory.defer((promise) => {
                _signupSessionState.password = password;
                promise.resolve();
            });
        };
        self.savePaymentIsComplete = (isComplete) => _promiseFactory.defer(deferredObject => {
            const isPaymentMethodCompleted = isComplete === true;
            _signupSessionRepository.savePaymentIsComplete(_authenticatedSignup.signupSessionId, _authenticatedSignup.secretKey, isPaymentMethodCompleted)
                .fail(deferredObject.reject)
                .done(() => {
                    _signupSessionState.signupDocument.isPaymentMethodCompleted = isPaymentMethodCompleted;
                    deferredObject.resolve();
                });
        });
        self.saveBillingMethod = (billingMethod) => {
            return _promiseFactory.defer(deferredObject => {
                _signupSessionRepository.saveBillingMethod(_authenticatedSignup.signupSessionId, _authenticatedSignup.secretKey, billingMethod)
                    .fail(deferredObject.reject)
                    .done(() => {
                        _signupSessionState.signupDocument.billingMethod = billingMethod;
                        deferredObject.resolve();
                    });
            });
        };
        self.savePaymentMethodAddressLineOne = (addressLineOne) => {
            return _promiseFactory.defer(deferredObject => {
                _signupSessionRepository.savePaymentMethodAddressLineOne(_authenticatedSignup.signupSessionId, _authenticatedSignup.secretKey, addressLineOne)
                    .fail(deferredObject.reject)
                    .done(() => {
                        _signupSessionState.signupDocument.paymentMethodAddressLineOne = addressLineOne;
                        deferredObject.resolve();
                    });
            });
        };
        self.savePaymentMethodAddressLineTwo = (addressLineTwo) => {
            return _promiseFactory.defer(deferredObject => {
                _signupSessionRepository.savePaymentMethodAddressLineTwo(_authenticatedSignup.signupSessionId, _authenticatedSignup.secretKey, addressLineTwo)
                    .fail(deferredObject.reject)
                    .done(() => {
                        _signupSessionState.signupDocument.paymentMethodAddressLineTwo = addressLineTwo;
                        deferredObject.resolve();
                    });
            });
        };
        self.savePaymentMethodCity = (city) => {
            return _promiseFactory.defer(deferredObject => {
                _signupSessionRepository.savePaymentMethodCity(_authenticatedSignup.signupSessionId, _authenticatedSignup.secretKey, city)
                    .fail(deferredObject.reject)
                    .done(() => {
                        _signupSessionState.signupDocument.paymentMethodCity = city;
                        deferredObject.resolve();
                    });
            });
        };
        self.savePaymentMethodExpirationDate = (expirationMonth, expirationYear) => {
            return _promiseFactory.defer(deferredObject => {
                _signupSessionRepository.savePaymentMethodExpirationDate(_authenticatedSignup.signupSessionId, _authenticatedSignup.secretKey, expirationMonth, expirationYear)
                    .fail(deferredObject.reject)
                    .done(() => {
                        _signupSessionState.signupDocument.paymentMethodExpirationMonth = expirationMonth;
                        _signupSessionState.signupDocument.paymentMethodExpirationYear = expirationYear;
                        deferredObject.resolve();
                    });
            });
        };
        self.savePaymentMethodHolderName = (cardHolderName) => {
            return _promiseFactory.defer(deferredObject => {
                _signupSessionRepository.savePaymentMethodHolderName(_authenticatedSignup.signupSessionId, _authenticatedSignup.secretKey, cardHolderName)
                    .fail(deferredObject.reject)
                    .done(() => {
                        _signupSessionState.signupDocument.paymentMethodHolderName = cardHolderName;
                        deferredObject.resolve();
                    });
            });
        };
        self.savePaymentMethodPostalCode = (countryCode, postalCode) => {
            return _promiseFactory.defer(deferredObject => {
                _signupSessionRepository.savePaymentMethodPostalCode(_authenticatedSignup.signupSessionId, _authenticatedSignup.secretKey, countryCode, postalCode)
                    .fail(deferredObject.reject)
                    .done(() => {
                        _signupSessionState.signupDocument.paymentMethodCountryCode = countryCode;
                        _signupSessionState.signupDocument.paymentMethodPostalCode = postalCode;
                        deferredObject.resolve();
                    });
            });
        };
        self.savePaymentMethodRegion = (countryCode, regionCode) => {
            return _promiseFactory.defer(deferredObject => {
                _signupSessionRepository.savePaymentMethodRegion(_authenticatedSignup.signupSessionId, _authenticatedSignup.secretKey, countryCode, regionCode)
                    .fail(deferredObject.reject)
                    .done(() => {
                        _signupSessionState.signupDocument.paymentMethodCountryCode = countryCode;
                        _signupSessionState.signupDocument.paymentMethodRegionCode = regionCode;
                        deferredObject.resolve();
                    });
            });
        };
        self.saveTimeZone = (timeZone) => {
            return _promiseFactory.defer(deferredObject => {
                _signupSessionRepository.saveTimeZone(_authenticatedSignup.signupSessionId, _authenticatedSignup.secretKey,timeZone)
                    .fail(deferredObject.reject)
                    .done(() => {
                        _signupSessionState.signupDocument.timeZone = timeZone;
                        deferredObject.resolve();
                    });
            });
        };

        self.isFraudulentPaymentInformation = (cardHolderName, cardNumberHashed, firstSixDigits, expirationDate, country, postalCode) => {
            const signupSessionId = _authenticatedSignup.signupSessionId;
            const secretKey = _authenticatedSignup.secretKey;
            return _signupSessionRepository.checkIsFraudulentPaymentInformation(signupSessionId,
                secretKey,
                cardHolderName,
                cardNumberHashed,
                firstSixDigits,
                expirationDate,
                country,
                postalCode);
        };

        self.saveSignupSessionStatus = (signupSessionStatus) => {
            return _promiseFactory.defer((promise) => {
                _signupSessionRepository.saveSignupSessionStatus(_authenticatedSignup.signupSessionId, _authenticatedSignup.secretKey, signupSessionStatus)
                    .fail(promise.reject)
                    .done(() => {
                        _signupSessionState.signupDocument.signupSessionStatus = signupSessionStatus;
                        promise.resolve();
                    });
            });
        };
        self.saveTitle = (title) => {
            return _promiseFactory.defer((promise) => {
                const signupSessionId = _authenticatedSignup.signupSessionId;
                const secretKey = _authenticatedSignup.secretKey;
                _signupSessionRepository.saveTitle(signupSessionId, secretKey, title)
                    .fail(promise.reject)
                    .done((signupSessionEntity) => {
                        _signupSessionState.signupDocument.title = signupSessionEntity.title;
                        promise.resolve();
                    });
            });
        };
        self.saveVerificationAnswer = (verificationAnswer) => {
            return _promiseFactory.defer((promise) => {
                _signupSessionRepository.saveVerificationAnswer(_authenticatedSignup.signupSessionId, _authenticatedSignup.secretKey, verificationAnswer)
                    .fail(promise.reject)
                    .done(() => {
                        _signupSessionState.signupDocument.verificationAnswer = verificationAnswer;
                        promise.resolve();
                    });
            });
        };
        self.saveVerificationQuestion = (verificationQuestionId) => {
            return _promiseFactory.defer((promise) => {
                _signupSessionRepository.saveVerificationQuestion(_authenticatedSignup.signupSessionId, _authenticatedSignup.secretKey, verificationQuestionId)
                    .fail(promise.reject)
                    .done(() => {
                        _signupSessionState.signupDocument.verificationQuestionId = verificationQuestionId;
                        promise.resolve();
                    });
            });
        };
        self.setEmailCompleted = (emailAddress, verificationCode) => {
            return _promiseFactory.defer((promise) => {
                const signupSessionId = _authenticatedSignup.signupSessionId;
                const secretKey = _authenticatedSignup.secretKey;
                _signupSessionRepository.setEmailCompleted(signupSessionId, secretKey, emailAddress, verificationCode)
                    .fail(promise.reject)
                    .done((signupSessionEntity) => {
                        _signupSessionState.signupDocument.emailAddress = signupSessionEntity.emailAddress;
                        _signupSessionState.signupDocument.isEmailCompleted = signupSessionEntity.isEmailCompleted;
                        _signupSessionState.signupDocument.emailVerificationCodeSendCount = signupSessionEntity.emailVerificationCodeSendCount;
                        _signupSessionState.signupDocument.emailVerificationFailureCount = signupSessionEntity.emailVerificationFailureCount;
                        promise.resolve(Object.assign({}, _signupSessionState.signupDocument));
                    });
            });
        };
        self.sendEmailVerificationCode = () => {
            return _promiseFactory.defer((promise) => {
                const signupSessionId = _authenticatedSignup.signupSessionId;
                const secretKey = _authenticatedSignup.secretKey;
                _signupSessionRepository.sendEmailVerificationCode(signupSessionId, secretKey)
                    .fail(promise.reject)
                    .done((signupSessionEntity) => {
                        _signupSessionState.signupDocument.isEmailCompleted = false;
                        _signupSessionState.signupDocument.emailVerificationCodeSendCount = signupSessionEntity.emailVerificationCodeSendCount;
                        promise.resolve(Object.assign({}, _signupSessionState.signupDocument));
                    });
            });
        };
        self.setPhoneNumberCompleted = (isPhoneNumberCompleted) => {
            return _promiseFactory.defer((promise) => {
                const signupSessionId = _authenticatedSignup.signupSessionId;
                const secretKey = _authenticatedSignup.secretKey;
                _signupSessionRepository.setPhoneNumberCompleted(signupSessionId, secretKey, isPhoneNumberCompleted)
                    .fail(promise.reject)
                    .done((signupSessionEntity) => {
                        _signupSessionState.signupDocument.isPhoneNumberCompleted = signupSessionEntity.isPhoneNumberCompleted;
                        promise.resolve();
                    });
            });
        };
        self.setProfileCompleted = (isProfileCompleted) => {
            return _promiseFactory.defer((promise) => {
                const {signupSessionId, secretKey} = _authenticatedSignup;
                _signupSessionRepository.setProfileCompleted(signupSessionId, secretKey, isProfileCompleted)
                    .fail(promise.reject)
                    .done((signupSessionEntity) => {
                        _signupSessionState.signupDocument.isProfileCompleted = signupSessionEntity.isProfileCompleted;
                        promise.resolve();
                    });
            });
        };
        self.setSecurityCompleted = (isSecurityCompleted) => {
            return _promiseFactory.defer((promise) => {
                const { signupSessionId, secretKey } = _authenticatedSignup;
                _signupSessionRepository.setSecurityCompleted(signupSessionId, secretKey, isSecurityCompleted)
                    .fail(promise.reject)
                    .done((signupSessionEntity) => {
                        _signupSessionState.signupDocument.isSecurityCompleted = signupSessionEntity.isSecurityCompleted;
                        promise.resolve();
                    });
            });
        };
        self.saveSearchAndNumberType = ({searchType, numberType}) => _promiseFactory.deferIndefinitely(promise => {
            const {signupSessionId, secretKey} = _authenticatedSignup;
            _signupSessionRepository.saveSearchAndNumberType({signupSessionId, secretKey, searchType, numberType})
                .fail(promise.reject)
                .done(() => {
                    _signupSessionState.signupDocument.reservedPhoneNumberSearchType = searchType;
                    promise.resolve();
                });
        });
        self.saveResellerAndClickId = (resellerId, clickId) => _promiseFactory.deferIndefinitely(promise => {
            const {signupSessionId, secretKey} = _authenticatedSignup;
            _signupSessionRepository.saveResellerAndClickId({signupSessionId, secretKey, resellerId, clickId})
                .fail(promise.reject)
                .done(() => {
                    _signupSessionState.signupDocument.resllerId = resellerId;
                    _signupSessionState.signupDocument.clickId = clickId;
                    promise.resolve();
                });
        });
        self.saveAttributionInfo = (affiliateUrl, hubSpotUtk, googleClickId, utmSource, utmMedium, utmCampaign, utmTerm, utmContent) => {
            return _promiseFactory.deferIndefinitely(promise => {
                const {signupSessionId, secretKey} = _authenticatedSignup;
                _signupSessionRepository.saveAttributionInfo({signupSessionId, secretKey, affiliateUrl, hubSpotUtk, googleClickId, utmSource, utmMedium, utmCampaign, utmTerm, utmContent})
                    .fail(promise.reject)
                    .done(() => {
                        if (googleClickId) {
                            _signupSessionState.signupDocument.clickId = googleClickId;
                        }
                        promise.resolve();
                    });
            });
        };
        self.savePlan = (planId) => {
            return _promiseFactory.deferIndefinitely(promise => {
                const { signupSessionId, secretKey } = _authenticatedSignup;
                _signupSessionRepository.savePlan({ signupSessionId, secretKey, planId })
                    .fail(promise.reject)
                    .done(() => {
                        _signupSessionState.signupDocument.planId = planId;
                        promise.resolve();
                    });
            });
        };
        self.saveTelephonyPackage = (telephonyPackageId) => {
            return _promiseFactory.deferIndefinitely(promise => {
                const { signupSessionId, secretKey } = _authenticatedSignup;
                _signupSessionRepository.saveTelephonyPackage({ signupSessionId, secretKey, telephonyPackageId })
                    .fail(promise.reject)
                    .done(() => {
                        _signupSessionState.signupDocument.telephonyPackageId = telephonyPackageId;
                        promise.resolve();
                    });
            });
        };
        self.saveLiveReceptionistPackage = (liveReceptionistPackageId) => {
            return _promiseFactory.deferIndefinitely(promise => {
                const { signupSessionId, secretKey } = _authenticatedSignup;
                _signupSessionRepository.saveLiveReceptionistPackage({ signupSessionId, secretKey, liveReceptionistPackageId })
                    .fail(promise.reject)
                    .done(() => {
                        _signupSessionState.signupDocument.liveReceptionistPackageId = liveReceptionistPackageId;
                        promise.resolve();
                    });
            });
        };
        self.saveWebChatPackage = (webChatPackageId) => {
            return _promiseFactory.deferIndefinitely(promise => {
                const { signupSessionId, secretKey } = _authenticatedSignup;
                _signupSessionRepository.saveWebChatPackage({ signupSessionId, secretKey, webChatPackageId })
                    .fail(promise.reject)
                    .done(() => {
                        _signupSessionState.signupDocument.webChatPackageId = webChatPackageId;
                        promise.resolve();
                    });
            });
        };
        self.saveOutboundCallingPackage = (outboundCallingPackageId) => {
            return _promiseFactory.deferIndefinitely(promise => {
                const { signupSessionId, secretKey } = _authenticatedSignup;
                _signupSessionRepository.saveOutboundCallingPackage({ signupSessionId, secretKey, outboundCallingPackageId })
                    .fail(promise.reject)
                    .done(() => {
                        _signupSessionState.signupDocument.outboundCallingPackageId = outboundCallingPackageId;
                        promise.resolve();
                    });
            });
        };
        self.setPlansCompleted = (isPlansCompleted) => {
            return _promiseFactory.defer((promise) => {
                const { signupSessionId, secretKey } = _authenticatedSignup;
                _signupSessionRepository.setPlansCompleted(signupSessionId, secretKey, isPlansCompleted)
                    .fail(promise.reject)
                    .done((signupSessionEntity) => {
                        _signupSessionState.signupDocument.isPlansCompleted = signupSessionEntity.isPlansCompleted;
                        promise.resolve();
                    });
            });
        };
        self.clearSessionStateReservedPhoneNumber = () => _clearSessionStateReservedPhoneNumber();

        self.getProgressPercentage = (createIfMissing = true) => {
            return _promiseFactory.defer((promise) => {
                if (!_signupSessionState.isLoaded) {
                    _loadSignupSession(createIfMissing)
                        .fail(promise.reject)
                        .done(() => {
                            promise.resolve(_signupSessionState.progressPercentage());
                        });
                } else {
                    promise.resolve(_signupSessionState.progressPercentage());
                }
            });
        };

        self.setProgressPercentage = (newPercentage) => {
            return _promiseFactory.defer((promise) => {
                if (newPercentage < 0 || newPercentage > 100) {
                    promise.reject("Percentage must be between 0 and 100.");
                    return;
                }

                _signupSessionState.progressPercentage(newPercentage);
                promise.resolve(_signupSessionState.progressPercentage());
            });
        };


        self.detached = function() {
            _signupSocket.disposeOfEvents();
        };
        self.init = () => {

            const SignupSessionRepository = require('persistence/repositories/signupRepository');
            _signupSessionRepository = new SignupSessionRepository();
            _signupSessionRepository.init();

            const SignupSessionDataProviderConstructor = require('persistence/dataProviders/signupDataProvider');
            _signupSessionDataProvider = new SignupSessionDataProviderConstructor();
            _signupSessionDataProvider.init();

            const SignupAuthenticationStoreConstructor = require('businessServices/authentication/stores/signupAuthenticationStore');
            _signupAuthenticationStore = new SignupAuthenticationStoreConstructor();
            _signupAuthenticationStore.init();

            _authenticatedSignup = require('common/authentication/authenticatedSignup');

            _signupSocket.subscribeToEvent("signupComplete", () => _loadSignupSession(false));
        };
    };
});
